import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';

import {BehaviorSubject, combineLatest, merge} from 'rxjs';
import {filter, map, take, takeUntil} from 'rxjs/operators';
import {BelegService, FilterBelegDTO, PageableBelegDTO, PageableBelegDTOSortingEnum} from '../../openapi/beleg-openapi';
import {Actions, ofType} from '@ngrx/effects';
import {BelegFilterTableSelectors} from '../../store/selectors/beleg-filter-table.selectors';
import {TableSettings} from '../../interfaces/table-setting.interface';
import {ZahlungDTO} from '../../openapi/zahlung-openapi';
import {AppContextSelectors} from '../../store/selectors/app-context.selectors';
import {AppState} from '../../interfaces/app-state.interface';
import {TransactionSelectors} from '../../store/selectors/transaction.selectors';
import {ZahlungSelectors} from '../../store/selectors/zahlung.selectors';
import {ZahlungActions} from '../../store/actions/zahlung.actions';
import {BelegSelectors} from '../../store/selectors/beleg.selectors';
import {BelegActions} from '../../store/actions/beleg.actions';
import {InhaberEntitiesSelectors} from '@adnova/jf-ng-components';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'zlng-preview-or-beleglos',
  templateUrl: './preview-or-beleglos.component.html',
  styleUrls: ['./preview-or-beleglos.component.scss'],
})
export class PreviewOrBeleglosComponent implements OnInit {
  private _store = inject(Store<AppState>);
  private _belegService = inject(BelegService);
  private _actions$ = inject(Actions);
  private _destroyRef = inject(DestroyRef);

  private _inhaberId = '';

  public hasBeleg?: boolean;
  public popupMode?: boolean;
  public inTransactionWizard = false;
  public angewiesen?: boolean;
  public isNotEditable?: boolean;

  public collectivePreview = new BehaviorSubject<{
    isCollectivePreview?: boolean;
    zahlungDto?: ZahlungDTO;
  }>({});

  constructor() {}

  ngOnInit(): void {
    this._store
      .select(ZahlungSelectors.zahlungInhaberId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(inhaberId => {
        if (!inhaberId) return;

        this._inhaberId = inhaberId;
      });

    this._store
      .select(AppContextSelectors.popupMode)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(popupMode => {
        this.popupMode = popupMode;
      });

    this._store
      .select(TransactionSelectors.finalPayments)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(finalPayments => {
        this.inTransactionWizard = !!finalPayments?.length;
      });

    this._store
      .select(ZahlungSelectors.angewiesen)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(angewiesen => {
        this.angewiesen = angewiesen;
      });

    this._store
      .select(ZahlungSelectors.notEditable)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(isNotEditable => {
        this.isNotEditable = isNotEditable;
      });

    /*
     * INFO: Zusammenfassen der Werte der Selektoren zu einem Objekt.
     * Änderung der BelegId oder des CollectivePreview sollen beide
     * unabhängig voneinander Einfluss auf die Anzeige des Belegs in der Preview haben.
     */
    merge(
      this._store.select(ZahlungSelectors.zahlungBelegId),
      this._store.select(TransactionSelectors.collectivePreview),
    )
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(data => {
          /*
           * INFO: Wenn data ein Objekt ist und kein (neues) Payment enthält,
           * kann der aktuelle Beleg in der Preview beibehalten werden.
           */
          if (data && typeof data === 'object' && !data.zahlungDto) {
            return false;
          }

          return true;
        }),
        map(data => {
          // INFO: Wenn data bereits ein zusammengeführtes Objekt ist, kann es einfach weitergereicht werden
          if (data && typeof data === 'object') return data;

          /*
           * INFO: Ansonsten beinhaltet data, die BelegId, welche bisher als String weitergereicht worden ist.
           * Ist dies der Fall, muss dieser dann in das erwartete Format gebracht werden.
           */
          return {
            isCollectivePreview: false,
            zahlungDto: {
              belegId: data,
              inhaberId: this._inhaberId,
            },
          } as {
            isCollectivePreview: boolean;
            zahlungDto: ZahlungDTO;
          };
        }),
      )
      .subscribe(data => {
        // INFO: Nur wenn sich die BelegId geändert hat, das CollectivPreview aktualisieren
        if (data.zahlungDto?.belegId !== this.collectivePreview.getValue().zahlungDto?.belegId) {
          this.collectivePreview.next(data);
        }
      });

    // INFO: Entscheiden, ob und wie die Preview angezeigt werden soll.
    this.collectivePreview.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(paymentData => {
      if (paymentData && paymentData.isCollectivePreview) {
        if (paymentData.zahlungDto?.inhaberId && paymentData.zahlungDto?.belegId) {
          this.hasBeleg = true;

          // FIXME: Errorhandling und in einen Effekt auslagern
          this._belegService
            .getBeleg(paymentData.zahlungDto.inhaberId, paymentData.zahlungDto.belegId)
            .subscribe(belegDto => {
              this._store.dispatch(
                BelegActions.loadContent({
                  belegDto,
                  closeable: false,
                }),
              );
            });
        } else {
          this.hasBeleg = false;
          this._store.dispatch(BelegActions.resetBelegPreview());
        }
      } else if (paymentData && !paymentData.isCollectivePreview) {
        if (paymentData.zahlungDto?.inhaberId && paymentData.zahlungDto.belegId) {
          this.hasBeleg = true;

          // FIXME Errorhandling und in einen Effekt auslagern
          this._belegService
            .getBeleg(paymentData.zahlungDto.inhaberId, paymentData.zahlungDto.belegId)
            .subscribe(belegDto => {
              this._store.dispatch(
                BelegActions.loadContent({
                  belegDto,
                  closeable: false,
                }),
              );

              /*
               * INFO: Sollte die Zahlung bereits von Anfang an mit einem Beleg verknüpft sein,
               * so muss die Edit-Form Component über das initiale Laden des Belegs informiert werden.
               */
              this._store.dispatch(
                ZahlungActions.initialAssignedBelegLoaded({
                  belegDto,
                }),
              );
            });
        } else {
          this.hasBeleg = false;
          this._store.dispatch(BelegActions.resetBelegPreview());
        }
      } else if (paymentData && paymentData.zahlungDto === null) {
        this.hasBeleg = false;
      } else {
        this.hasBeleg = undefined;
      }
    });

    // INFO: Wenn ein Beleg zugeordnet wurde, wird der Dialog geschlossen.
    this._store
      .select(BelegSelectors.belegToAssignSelected)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(belegDto => {
        if (!belegDto) return;

        this._store
          .select(BelegSelectors.isBelegFilterDialogOpen)
          .pipe(
            take(1),
            filter(isOpen => isOpen),
          )
          .subscribe(() => {
            this._store.dispatch(
              ZahlungActions.assignBelegToZahlung({
                belegDto,
              }),
            );
            this._store.dispatch(BelegActions.closeBelegFilterDialog());
          });
      });

    // INFO: Wenn der Dialog geöffnet wird, initDialog() aufrufen um die Belege zu laden.
    this._store
      .select(BelegSelectors.isBelegFilterDialogOpen)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(isOpen => isOpen),
      )
      .subscribe(() => {
        /*
         * INFO: Wenn das Popup geöffnet wird, werden initial die Filter und die Pagination zurückgesetzt.
         * Das geschieht hier und nicht in der Komponente selbst, damit es perspektivisch möglich ist,
         * das Popup auch mit (von außen) voreingestellten Filtern zu öffnen.
         */
        setTimeout(() => {
          this.initDialog();
        }, 0);
      });
  }

  initDialog(): void {
    // INFO: Aktuelle InhaberID und Pagination auslesen. Anschließend die passenden Belege laden.
    this._store
      .select(InhaberEntitiesSelectors.currentInhaberId)
      .pipe(take(1))
      .subscribe(inhaberId => {
        combineLatest([
          this._store.select(BelegSelectors.pagination),
          this._store.select(BelegSelectors.filter),
          this._store.select(BelegFilterTableSelectors.tableSettings),
        ])
          .pipe(takeUntil(this._actions$.pipe(ofType(BelegActions.closeBelegFilterDialog))))
          .subscribe(([pagination, belegFilter, tableSettings]) => {
            if (!inhaberId) return;

            // INFO: Filter setzen
            const filterBelegDTO: FilterBelegDTO = {
              textfilter: belegFilter.text?.split(' ') || undefined,
              datumVon: belegFilter.dateVon || undefined,
              datumBis: belegFilter.dateBis || undefined,
              betragVon: belegFilter.betragVon || undefined,
              betragBis: belegFilter.betragBis || undefined,
              offen: true,
              bearbeitet: true,
              festgeschrieben: true,
            };

            // INFO: Pagination setzen
            const pageableBelegDTO: PageableBelegDTO = {
              offset: pagination.offset,
              limit: pagination.size,
              sorting: this.tableSettings2sortingEnum(tableSettings),
            };

            // TODO: Das sollte in einen Effect, ausserdem fehlt Errorhandling
            const countBelege = this._belegService.countBelege(inhaberId, filterBelegDTO);

            // TODO: Das sollte in einen Effect, ausserdem fehlt Errorhandling
            const readBelege = this._belegService.readBelege(inhaberId, filterBelegDTO, pageableBelegDTO);

            combineLatest([countBelege, readBelege]).subscribe(([count, belege]) => {
              // INFO: Geladene Belege dispatchen
              this._store.dispatch(BelegActions.loadData({data: belege, count: count.count}));
            });
          });
      });
  }

  private tableSettings2sortingEnum(tableSettings: TableSettings): PageableBelegDTOSortingEnum[] {
    const enums: PageableBelegDTOSortingEnum[] = [];

    for (let i = 0; i < tableSettings._sortParams.length; i++) {
      const column = tableSettings._columns.find(col => col.id === tableSettings._sortParams[i]);
      if (column?.sortIds) {
        const enumValue = tableSettings._sortDirs[i] === 'asc' ? column.sortIds.asc : column.sortIds.desc;
        const matched = Object.values(PageableBelegDTOSortingEnum).find(v => v === enumValue);
        if (matched) {
          enums.push(matched);
        }
      }
    }

    return enums;
  }

  openBelegChooser(): void {
    this._store.dispatch(BelegActions.openBelegFilterDialog());
  }
}
